import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useDidMount } from 'src/hooks/useDidMount';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import {
  RecipientDetailDto,
  RecipientDto
} from 'src/store/recipients/recipient';
import { mapRecipientDetailToEdit } from 'src/store/recipients/recipient.map';
import {
  createRecipient,
  deleteRecipient,
  editRecipient,
  fetchRecipients
} from 'src/store/recipients/recipients.actions';
import { RecipientApi } from 'src/store/recipients/recipients.api';
import { selectRecipients } from 'src/store/recipients/recipients.selectors';
import { isLoadingRecipients } from 'src/store/requests/requests.selectors';
import { RecipientForm } from './RecipientForm';

export const RecipientsPage = () => {
  const [currentRecipient, setCurrentRecipient] = useState<RecipientDto | null>(
    null
  );
  const [recipient, setRecipient] =
    useState<Nullable<RecipientDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const recipients = useSelector(selectRecipients);
  const loadingRecipients = useSelector(isLoadingRecipients);

  useDidMount(() => {
    dispatch(fetchRecipients());
  }, []);

  useEffect(() => {
    if (currentRecipient) {
      setShowDeleteModal(true);
    }
  }, [currentRecipient]);

  const onCancel = () => {
    setCurrentRecipient(null);
    setRecipient(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (recipient: RecipientDto) => {
    const response = await RecipientApi.getById(recipient.id);
    if (response.ok) {
      setRecipient(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (recipient: RecipientDto) => {
    setCurrentRecipient(recipient);
  };

  const onConfirmDelete = () => {
    if (currentRecipient) {
      dispatch(deleteRecipient(currentRecipient.id, currentRecipient));
      setCurrentRecipient(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (recipient: RecipientDetailDto) => {
    const dto = mapRecipientDetailToEdit(recipient);
    if (dto.id) {
      await dispatch(editRecipient(dto.id, dto));
      onCancel();
    } else {
      await dispatch(createRecipient(dto));
      onCancel();
    }
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('recipient.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('recipient.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('recipient.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={recipients}
        loading={loadingRecipients}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'no', dir: 'asc' }]}
      >
        <Column
          field="no"
          title={t('props.number')}
          footerCell={() => (
            <GridCountCell
              count={recipients?.length}
              label={t('recipient.plural')}
            />
          )}
        />
        <Column field="name" title={t('props.name')} />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        header={`${recipient ? t('edit') : t('create.verb')} ${t(
          'recipient.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <RecipientForm
          recipient={recipient}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('recipient.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('recipient.delete')} <q>{currentRecipient?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
